// Gedeelde lightbox voor galerijbeelden (MAT-930). Een klik of tik op een beeld in een galerij opent
// het in een native <dialog>, met vorige/volgende, vegen, Esc en een sluitknop. Het script kent de
// galerijen alleen aan hun container; een nieuwe galerij doet mee met data-ms-lightbox. Beelden in een
// link of knop, video's en de 3D-viewer blijven buiten schot. Geen dependencies, één bestand.
(() => {
  const GALLERIES = [
    '[data-ms-lightbox]',
    '.mood-gallery-grid', // sfeergalerij onder Libeccio, BORA, Isola en de nachtkastjes
    '#ms-nachtkast .stage', // groot beeld van de nachtkastwerkplek
    '.ms-isola-examples__grid',
    '.msc-hero', // scènes bovenaan een collectiepagina
    '.ms-lookbook .project-rail',
    '.ms-lookbook .details-section',
  ].join(',');
  const TRIGGER = `:is(${GALLERIES}) img`;
  const WIDTHS = [800, 1200, 1600, 2000, 2400, 3000];
  const TEXT = /^nl\b/i.test(document.documentElement.lang || 'nl')
    ? {open: 'Vergroot', dialog: 'Vergrote afbeelding', close: 'Sluiten', prev: 'Vorige afbeelding', next: 'Volgende afbeelding'}
    : {open: 'Enlarge', dialog: 'Enlarged image', close: 'Close', prev: 'Previous image', next: 'Next image'};

  const style = document.createElement('style');
  style.textContent = `
${TRIGGER}{cursor:zoom-in}
${TRIGGER}:focus-visible{outline:2px solid currentColor;outline-offset:-4px}
.ms-lightbox{width:100%;height:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;background:rgba(18,18,16,.94);color:#fff;overscroll-behavior:contain}
.ms-lightbox::backdrop{background:rgba(18,18,16,.94)}
.ms-lightbox figure{box-sizing:border-box;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;width:100%;height:100%;margin:0;padding:64px 72px 24px}
.ms-lightbox img{display:block;min-height:0;max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;touch-action:pan-y pinch-zoom;user-select:none}
.ms-lightbox figcaption{flex:none;max-width:72ch;margin:0;font-size:14px;line-height:1.4;text-align:center;color:rgba(255,255,255,.86)}
.ms-lightbox button{position:absolute;display:grid;place-items:center;width:48px;height:48px;margin:0;padding:0;border:0;border-radius:50%;background:rgba(255,255,255,.12);color:#fff;font-size:28px;line-height:1;cursor:pointer}
.ms-lightbox button:focus-visible{outline:2px solid #fff;outline-offset:2px}
.ms-lightbox button[hidden]{display:none}
.ms-lightbox [data-close]{top:12px;right:12px}
.ms-lightbox [data-step]{top:50%;transform:translateY(-50%)}
.ms-lightbox [data-step="-1"]{left:12px}
.ms-lightbox [data-step="1"]{right:12px}
@media(hover:hover){.ms-lightbox button:hover{background:rgba(255,255,255,.24)}}
@media(max-width:749px){.ms-lightbox figure{padding:64px 0 76px}.ms-lightbox [data-step]{top:auto;bottom:16px;transform:none}}`;
  document.head.append(style);

  let dialog, image, caption, prev, next, items = [], index = 0, trigger = null;

  // Een Shopify-beeld komt vers van de CDN op de breedte van het scherm; ander beeld houdt zijn eigen srcset.
  function sources(img) {
    const src = img.currentSrc || img.src;
    let url;
    try {
      url = new URL(src, location.href);
    } catch {
      return {src, srcset: ''};
    }
    if (url.hostname !== 'cdn.shopify.com' && !url.pathname.startsWith('/cdn/shop/')) return {src, srcset: img.srcset};
    const at = width => {
      const sized = new URL(url);
      for (const key of ['width', 'height', 'crop']) sized.searchParams.delete(key);
      sized.searchParams.set('width', String(width));
      return sized.href;
    };
    return {src: at(1600), srcset: WIDTHS.map(width => `${at(width)} ${width}w`).join(', ')};
  }

  const captionOf = img => img.closest('figure')?.querySelector('figcaption')?.textContent.trim() || '';
  // Tijdens de overgang van de sfeergalerij ligt het nieuwe beeld nog onzichtbaar bovenop; tel het niet mee.
  // Verborgen dia's van een diavoorstelling tellen wel mee; weggefilterde kaarten ([data-group][hidden],
  // projecten op de homepage) niet.
  const gallery = container => [...container.querySelectorAll('img')].filter(img =>
    img.matches(TRIGGER) && !img.closest('a, button, video, [data-group][hidden]') && !img.classList.contains('is-incoming'));

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'ms-lightbox';
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', TEXT.dialog);
    dialog.innerHTML = `<button type="button" data-close aria-label="${TEXT.close}">×</button>
<figure><img alt="" decoding="async" sizes="100vw"><figcaption aria-live="polite"></figcaption></figure>
<button type="button" data-step="-1" aria-label="${TEXT.prev}">‹</button>
<button type="button" data-step="1" aria-label="${TEXT.next}">›</button>`;
    image = dialog.querySelector('img');
    caption = dialog.querySelector('figcaption');
    [prev, next] = dialog.querySelectorAll('[data-step]');
    let start = null, swiped = false;
    dialog.addEventListener('click', event => {
      // Na een veeg volgt soms nog een klik; die mag de lightbox niet sluiten.
      if (swiped) {
        swiped = false;
        return;
      }
      const step = event.target.closest('[data-step]');
      if (step) show(index + Number(step.dataset.step));
      else if (!event.target.closest('img, figcaption')) dialog.close();
    });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) event.preventDefault();
    });
    // Scroll-lock zonder overflow:hidden op <html>: dat liet de homepage op 1440 px met een gewone
    // schuifbalk ruim 3000 px verspringen. De pagina blijft staan omdat scrollen boven de lightbox niets doet.
    // Knijpen om te zoomen (twee vingers) blijft werken.
    // Ctrl+wiel is knijpen op een trackpad: dat blijft zoomen.
    dialog.addEventListener('wheel', event => {
      if (!event.ctrlKey) event.preventDefault();
    }, {passive: false});
    dialog.addEventListener('touchmove', event => {
      if (event.touches.length < 2) event.preventDefault();
    }, {passive: false});
    dialog.addEventListener('pointerdown', event => {
      start = event.pointerType === 'mouse' ? null : {x: event.clientX, y: event.clientY};
      swiped = false;
    });
    dialog.addEventListener('pointerup', event => {
      if (!start) return;
      const dx = event.clientX - start.x, dy = event.clientY - start.y;
      start = null;
      swiped = Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy);
      if (swiped) show(index + (dx < 0 ? 1 : -1));
    });
    dialog.addEventListener('pointercancel', () => { start = null; });
    dialog.addEventListener('close', () => {
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      trigger?.focus({preventScroll: true});
      trigger = null;
    });
    document.body.append(dialog);
  }

  function show(at) {
    index = (at + items.length) % items.length;
    const img = items[index];
    const {src, srcset} = sources(img);
    const text = captionOf(img);
    image.removeAttribute('src');
    image.srcset = srcset;
    image.src = src;
    image.alt = img.alt || text;
    const count = items.length > 1 ? `${index + 1} / ${items.length}` : '';
    caption.textContent = [text, count].filter(Boolean).join(' · ');
    prev.hidden = next.hidden = items.length < 2;
  }

  function open(img) {
    const container = img.closest(GALLERIES);
    items = gallery(container);
    if (!items.length) return;
    if (!dialog) build();
    trigger = img;
    // Klik op een beeld dat net invloeit: neem het zichtbare beeld in dezelfde cel.
    show(Math.max(0, items.findIndex(item => item === img || item.parentElement === img.parentElement)));
    dialog.showModal();
  }

  // Galerijen vervangen hun beelden (afwerking wisselen), dus bereikbaarheid met het toetsenbord zetten
  // we op elk nieuw beeld opnieuw.
  function decorate(root) {
    for (const img of root.querySelectorAll?.(TRIGGER) ?? []) {
      if (img.closest('a, button, video') || img.hasAttribute('tabindex')) continue;
      img.tabIndex = 0;
      img.setAttribute('role', 'button');
      img.setAttribute('aria-haspopup', 'dialog');
      if (!img.alt) img.setAttribute('aria-label', `${TEXT.open}: ${captionOf(img) || TEXT.dialog}`);
    }
  }

  document.addEventListener('click', event => {
    const img = event.target.closest?.(TRIGGER);
    if (!img || img.closest('a, button, video') || img.closest('.ms-lightbox')) return;
    event.preventDefault();
    open(img);
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const img = event.target.closest?.(TRIGGER);
    if (!img || img.closest('a, button, video, .ms-lightbox')) return;
    event.preventDefault();
    open(img);
  });
  decorate(document);
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) decorate(node.matches(TRIGGER) ? node.parentElement : node);
  }).observe(document.body, {childList: true, subtree: true});
})();
